import { AxiosResponse } from "axios";
import { getAxiosInstance } from "./api-utils";
import { CustomerHit } from "@/model/customer-hit-schema";
import { Customer } from "@/model/customer";
import { UUIDTypes } from "uuid";
import { Paged, PAGE_SIZE } from "@/model/paged";

const CUSTOMERS = "/customers";

export async function searchForCustomers(keywords: string, page = 0, size = PAGE_SIZE): Promise<Paged<CustomerHit>> {
    const response: AxiosResponse<Paged<CustomerHit>> = await getAxiosInstance().get(CUSTOMERS, { params: { keywords, page, size } });
    return response.data;
}

export async function getCustomer(id: number): Promise<Customer> {
    const response: AxiosResponse<Customer> = await getAxiosInstance().get(`${CUSTOMERS}/${id}`);
    return response.data;
}

export async function saveCustomer(model: { object: Customer, initialObject: Customer }) {
    const response = await getAxiosInstance().patch(`${CUSTOMERS}/${model.object.id}`, model.object);
    if (response.status === 200) {
        // Attach new files
        const files = model.object.attachedFiles.map(file => file.file).filter(file => !(!file));
        await Promise.all(files.map(file => uploadAttachmentToCustomer(model.object.id, file)));

        // Remove removed files
        const removedFiles = model.initialObject.attachedFiles.filter(file => !model.object.attachedFiles.some(f => f.uuid === file.uuid));
        await Promise.all(removedFiles.map(file => deleteAttachmentFromCustomer(model.object.id, file.uuid)));
    }
    return response.data;
}

export async function createCustomer(customer: Customer) {
    const response: AxiosResponse<Customer> = await getAxiosInstance().put(CUSTOMERS, customer);
    return response.data.id;
}

export async function uploadAttachmentToCustomer(customerId: number, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("objectId", customerId.toString());

    await getAxiosInstance({ excludeContentType: true }).post(`${CUSTOMERS}/${customerId}/_uploadAttachment`, formData);
}

export async function deleteAttachmentFromCustomer(customerId: number, attachmentId: UUIDTypes) {
    await getAxiosInstance().delete(`${CUSTOMERS}/${customerId}/attachments/${attachmentId}`);
}

export async function downloadAttachmentFromCustomer(customerId: number, attachmentId: UUIDTypes) {
    const url = await previewAttachmentFromCustomer(customerId, attachmentId);

    // Open in new tab (browser decides to display or download)
    window.open(url, "_blank");

    // Optionally revoke the URL after some time to free memory
    setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export async function previewAttachmentFromCustomer(customerId: number, attachmentId: UUIDTypes) {
    const res = await getAxiosInstance().get(`${CUSTOMERS}/${customerId}/attachments/${attachmentId}`, {
        responseType: 'blob'
    });

    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const url = URL.createObjectURL(blob);

    return url;
}